import React from 'react';
import styled from 'styled-components';
import { STANDARD_RATE_USD_PER_WU } from '../config/workUnits';
import Pr0Unit from './common/Pr0Unit';

export interface TokenRateTileProps {
  className?: string;
  variant?: 'default' | 'compact';
  title?: React.ReactNode; // override title if embedding elsewhere
}

const Tile = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.08);
  border: 1px solid var(--brand-line);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  row-gap: 0.65rem;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;

  &:hover {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 0.75rem 2.5rem rgba(0, 0, 0, 0.12);
    border-color: var(--brand-gold);
  }

  &:active {
    transform: translateY(0) scale(0.998);
  }

  &:focus-within {
    outline: none;
    box-shadow: 0 0 0 3px rgba(200, 167, 94, 0.15), 0 0.75rem 2.5rem rgba(0, 0, 0, 0.12);
    border-color: #E8DFC4;
  }

  @media (min-width: 768px) {
    padding: 1.75rem 2rem;
    border-radius: 1.25rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--brand-black);
  font-size: 1.125rem;
  font-weight: 600;
  font-family: 'Space Grotesk', sans-serif;

  @media (min-width: 768px) { font-size: 1.25rem; }
`;

const ContentRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const Label = styled.span`
  color: var(--brand-text-secondary);
`;

const Equals = styled.span`
  color: var(--brand-text-secondary);
  font-weight: 600;
`;

const PricePill = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  background: linear-gradient(180deg, var(--brand-canvas) 0%, #ffffff 100%);
  border: 1px solid var(--brand-line);
  border-radius: 999px;
  box-shadow: 0 0.25rem 1rem rgba(200, 167, 94, 0.08);
  transition: background 180ms ease, box-shadow 180ms ease, transform 120ms ease;

  &:hover {
    background: linear-gradient(180deg, #FBF8F0 0%, #ffffff 100%);
    box-shadow: 0 0.5rem 1.25rem rgba(200, 167, 94, 0.12);
  }

  &:active { transform: scale(0.997); }
`;

const Price = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--brand-text);
  font-variant-numeric: tabular-nums;

  @media (max-width: 640px) {
    font-size: 1.4rem;
  }
`;

const Suffix = styled.span`
  color: var(--brand-text-secondary);
  font-size: 0.95rem;
  margin-left: 0.25rem;
  text-transform: lowercase;
`;

const ScopeNote = styled.span`
  color: var(--brand-text-secondary);
  font-size: 0.85rem;
  text-transform: lowercase;
`;

/**
 * Standard Rate tile (2026-08 PRO Work Units migration).
 *
 * Replaces the live pr0 exchange-rate tile: Work Units convert at the
 * PUBLISHED Standard Rate — a fixed $2.18 per WU for on-demand, overtime,
 * and top-ups — so there is no live Firestore rate listener here anymore.
 * The wallet's internal denomination (and its live rate plumbing) is
 * unchanged; this tile is display-only.
 */
const TokenRateTile: React.FC<TokenRateTileProps> = ({ className, variant = 'default', title }) => {
  return (
    <Tile className={className} aria-label="standard rate">
      <HeaderRow>
        <Title>{title || 'standard rate'}</Title>
      </HeaderRow>
      <ContentRow>
        <Label>1 <Pr0Unit /></Label>
        <Equals>=</Equals>
        <PricePill>
          <Price>${STANDARD_RATE_USD_PER_WU.toFixed(2)}</Price><Suffix>usd</Suffix>
        </PricePill>
      </ContentRow>
      <ScopeNote>on-demand, overtime &amp; top-ups</ScopeNote>
    </Tile>
  );
};

export default TokenRateTile;
